import _ from 'lodash'
import moment from 'moment-timezone'

const isValidDate = (date) => moment(date, 'YYYY-MM-DD', true).isValid()

const isValidDateTime = (date) => _.isString(date) && moment(date, moment.ISO_8601).isValid()

const TIMESTAMP_BOUNDARIES = [1451599200000, 4102437600000] // [2016-01-01, 2100-01-01]
const isValidTimestamp = (timestamp) =>
  _.isNumber(timestamp) && timestamp > TIMESTAMP_BOUNDARIES[0] && timestamp < TIMESTAMP_BOUNDARIES[1]

const getValidTimezone = (timezone) => (_.isString(timezone) && moment.tz.zone(timezone) ? timezone : undefined)

// Normalize a calendar-date input (YYYY-MM-DD string or Date from Apollo's Date
// scalar, which is UTC midnight) into a moment in the patient TZ.
const getDateMoment = (date, timezone) => {
  let dateString
  if (_.isString(date) && isValidDate(date)) {
    dateString = date
  } else if (_.isDate(date) && !Number.isNaN(date.valueOf())) {
    dateString = date.toISOString().slice(0, 10)
  } else {
    return null
  }
  return timezone ? moment.tz(dateString, 'YYYY-MM-DD', true, timezone) : moment(dateString, 'YYYY-MM-DD', true)
}

/**
 * Helper class for formatting dates in Meru Health programs.
 *
 * Date is relative to the provided time zone, or client time zone by default.
 *
 * The day changes at 3 AM in Meru Health program.
 */
class MeruDate {
  static now(timezone) {
    const meruDate = new MeruDate()
    meruDate.timezone = getValidTimezone(timezone)
    meruDate.moment = meruDate.timezone
      ? moment().tz(meruDate.timezone).subtract(3, 'hours')
      : moment().subtract(3, 'hours')
    return meruDate
  }

  constructor(date, timezone) {
    this.timezone = getValidTimezone(timezone)
    const dateMoment = getDateMoment(date, this.timezone)

    if (dateMoment) {
      this.moment = dateMoment
    } else if (isValidDateTime(date)) {
      this.moment = this.timezone
        ? moment.tz(date, moment.ISO_8601, this.timezone).subtract(3, 'hours')
        : moment(date, moment.ISO_8601).subtract(3, 'hours')
    } else if (isValidTimestamp(date)) {
      this.moment = this.timezone
        ? moment.tz(date, this.timezone).subtract(3, 'hours')
        : moment(date).subtract(3, 'hours')
    } else {
      this.moment = null
    }
  }

  formatDate() {
    return this.moment ? this.moment.format('l') : null
  }

  formatDayNumber(programStartDate) {
    if (!this.moment || !isValidDate(programStartDate)) {
      return null
    }
    const dayNumber = this.moment.clone().add(1, 'day').diff(getDateMoment(programStartDate, this.timezone), 'days')
    return dayNumber > 0 ? dayNumber : 0
  }

  formatISODate() {
    return this.moment ? this.moment.format('YYYY-MM-DD') : null
  }

  formatMonthDay() {
    return this.moment ? this.moment.format('MMM Do') : null
  }

  formatWeekDayNumber(programStartDate) {
    if (!this.moment || !isValidDate(programStartDate)) {
      return null
    }
    return ((this.moment.day() + 7 - getDateMoment(programStartDate, this.timezone).day()) % 7) + 1
  }

  formatWeekNumber(programStartDate, showNegative = false) {
    if (!this.moment || !isValidDate(programStartDate)) {
      return null
    }
    // Add 1 week as at Meru the week of programStartDate is week 1
    const relativeWeekNumber =
      Math.floor(this.moment.diff(getDateMoment(programStartDate, this.timezone), 'weeks', true)) + 1
    return showNegative ? relativeWeekNumber : Math.max(relativeWeekNumber, 0)
  }

  subtract(...args) {
    if (this.moment) {
      this.moment.subtract(...args)
    }
    return this
  }

  valueOf() {
    return this.moment ? this.moment.valueOf() : null
  }

  hasProgramStarted(programStartDate) {
    return this.formatDayNumber(programStartDate) > 0
  }

  /**
   *
   * @param {string|undefined} programStartDate
   * @param {number} lastWeekNbr
   * @returns {number|null}
   */
  daysFromProgramEnd(programStartDate, lastWeekNbr) {
    // Using with "new MeruDate(date) without time will give one day less than expected (see tests)
    // Has expected behavior with MeruDate.now()
    const currentDayNumber = this.formatDayNumber(programStartDate)
    if (!currentDayNumber) return null
    const lastDayNumber = lastWeekNbr * 7
    return currentDayNumber - lastDayNumber
  }
}

export default MeruDate
